import React, { useEffect, useState, useCallback } from "react";
import {
  Users, Plus, Phone, Mail, Trash2, X, ChevronRight, Calendar,
  Tag, MessageSquare, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";

const STAGES = [
  { key: "new", label: "New Leads", color: "border-t-blue-400", badge: "bg-blue-100 text-blue-700", dot: "bg-blue-400" },
  { key: "contacted", label: "Contacted", color: "border-t-amber-400", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  { key: "converted", label: "Converted", color: "border-t-green-400", badge: "bg-green-100 text-green-700", dot: "bg-green-400" },
  { key: "lost", label: "Lost", color: "border-t-slate-300", badge: "bg-slate-100 text-slate-500", dot: "bg-slate-300" },
];

const SOURCES = [
  { value: "manual", label: "Manual" },
  { value: "review_request", label: "Review Request" },
  { value: "qr_scan", label: "QR Scan" },
  { value: "gbp_message", label: "GBP Message" },
  { value: "other", label: "Other" },
];

const sourceLabel = (s) => SOURCES.find((x) => x.value === s)?.label || s;

const sourceBadgeColor = (s) => ({
  manual: "bg-slate-100 text-slate-600",
  review_request: "bg-purple-100 text-purple-700",
  qr_scan: "bg-cyan-100 text-cyan-700",
  gbp_message: "bg-brand-100 text-brand-700",
  other: "bg-slate-100 text-slate-500",
}[s] || "bg-slate-100 text-slate-600");

function relativeDate(d) {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - Date.now()) / 86400000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return "Today";
  return `In ${diff}d`;
}

const emptyForm = { name: "", phone: "", email: "", source: "manual", service: "", value: "", followUpAt: "" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // open detail panel
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: { limit: 200 } });
      setLeads(arr(data?.data));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byStage = (key) => leads.filter((l) => l.stage === key);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/leads", { ...form, value: form.value ? Number(form.value) : 0 });
      setLeads((prev) => [data, ...prev]);
      setShowAdd(false);
      setForm(emptyForm);
      toast.success("Lead added");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleStage = async (lead, newStage) => {
    try {
      const { data } = await api.put(`/leads/${lead.id || lead._id}`, { stage: newStage });
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      if (selected?.id === data.id) setSelected(data);
      toast.success(`Moved to ${STAGES.find((s) => s.key === newStage)?.label}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    try {
      await api.delete(`/leads/${lead.id || lead._id}`);
      setLeads((prev) => prev.filter((l) => l.id !== (lead.id || lead._id)));
      if (selected?.id === lead.id) setSelected(null);
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      const { data } = await api.post(`/leads/${selected.id || selected._id}/notes`, { note: noteText });
      setSelected(data);
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      setNoteText("");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const totalConverted = byStage("converted").length;
  const totalLeads = leads.length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads & CRM"
        subtitle="Track leads from review requests, QR scans, and GBP messages"
        icon={Users}
        actions={
          <Button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus className="h-4 w-4" /> Add Lead
          </Button>
        }
      />

      {/* Summary Strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STAGES.map((s) => {
          const count = byStage(s.key).length;
          return (
            <div key={s.key} className={`card-surface p-4 border-t-4 ${s.color}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className="mt-1 text-3xl font-bold text-slate-900">{count}</p>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Loading…</div>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No leads yet"
          description="Add your first lead manually, or they'll appear here automatically from review requests and QR scans."
        />
      ) : (
        /* Kanban Board */
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {STAGES.map((stage) => {
            const stageleads = byStage(stage.key);
            return (
              <div key={stage.key} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${stage.dot}`} />
                  <span className="text-sm font-semibold text-slate-700">{stage.label}</span>
                  <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{stageleads.length}</span>
                </div>
                <div className="flex flex-col gap-2 min-h-[80px]">
                  {stageleads.map((lead) => (
                    <LeadCard
                      key={lead.id || lead._id}
                      lead={lead}
                      stages={STAGES}
                      onOpen={() => setSelected(lead)}
                      onStage={handleStage}
                      onDelete={handleDelete}
                    />
                  ))}
                  {stageleads.length === 0 && (
                    <div className="rounded-xl border-2 border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
                      No leads here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Lead Modal */}
      {showAdd && (
        <Modal title="Add Lead" onClose={() => { setShowAdd(false); setForm(emptyForm); }}>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="form-label">Name *</label>
                <input required className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name" />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
              </div>
              <div>
                <label className="form-label">Source</label>
                <select className="form-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Service / Product</label>
                <input className="form-input" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="e.g. Dental Cleaning" />
              </div>
              <div>
                <label className="form-label">Deal Value (₹)</label>
                <input type="number" className="form-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label className="form-label">Follow-up Date</label>
                <input type="date" className="form-input" value={form.followUpAt} onChange={(e) => setForm({ ...form, followUpAt: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setForm(emptyForm); }}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add Lead"}</Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Detail Panel */}
      {selected && (
        <LeadDetail
          lead={selected}
          stages={STAGES}
          noteText={noteText}
          setNoteText={setNoteText}
          onStage={handleStage}
          onDelete={handleDelete}
          onAddNote={handleAddNote}
          onClose={() => { setSelected(null); setNoteText(""); }}
        />
      )}
    </div>
  );
}

function LeadCard({ lead, stages, onOpen, onStage, onDelete }) {
  const followUp = lead.followUpAt ? relativeDate(lead.followUpAt) : null;
  const isOverdue = followUp?.includes("overdue");
  return (
    <div
      className="card-surface p-3 cursor-pointer hover:shadow-md transition-shadow group"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 text-sm truncate">{lead.name}</p>
          {lead.service && <p className="text-xs text-slate-500 truncate">{lead.service}</p>}
        </div>
        <button
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity shrink-0 p-0.5"
          onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${sourceBadgeColor(lead.source)}`}>
          {sourceLabel(lead.source)}
        </span>
        {followUp && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold flex items-center gap-0.5 ${isOverdue ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}>
            <Calendar className="h-2.5 w-2.5" /> {followUp}
          </span>
        )}
        {lead.notes?.length > 0 && (
          <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 flex items-center gap-0.5">
            <MessageSquare className="h-2.5 w-2.5" /> {lead.notes.length}
          </span>
        )}
      </div>
      <div className="mt-2 flex gap-1.5">
        {lead.phone && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{lead.phone}</span>}
        {lead.email && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />{lead.email}</span>}
      </div>
    </div>
  );
}

function LeadDetail({ lead, stages, noteText, setNoteText, onStage, onDelete, onAddNote, onClose }) {
  const currentStageIdx = stages.findIndex((s) => s.key === lead.stage);
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex w-full max-w-md flex-col bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-bold text-slate-900">{lead.name}</h2>
            <p className="text-xs text-slate-400">{sourceLabel(lead.source)}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 p-5">
          {/* Contact Info */}
          <div className="rounded-xl bg-slate-50 p-4 space-y-2">
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Phone className="h-4 w-4 text-slate-400" />
                <a href={`tel:${lead.phone}`} className="hover:text-brand-600">{lead.phone}</a>
              </div>
            )}
            {lead.email && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Mail className="h-4 w-4 text-slate-400" />
                <a href={`mailto:${lead.email}`} className="hover:text-brand-600">{lead.email}</a>
              </div>
            )}
            {lead.service && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Tag className="h-4 w-4 text-slate-400" /> {lead.service}
              </div>
            )}
            {lead.value > 0 && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="text-slate-400 font-mono text-xs">₹</span>
                <span className="font-semibold">₹{lead.value.toLocaleString()}</span>
              </div>
            )}
            {lead.followUpAt && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>Follow-up: {new Date(lead.followUpAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Stage Pipeline */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Pipeline Stage</p>
            <div className="flex items-center gap-1">
              {stages.map((s, i) => (
                <React.Fragment key={s.key}>
                  <button
                    onClick={() => onStage(lead, s.key)}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-all ${
                      lead.stage === s.key
                        ? `${s.badge} ring-2 ring-offset-1 ring-current`
                        : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                    }`}
                  >
                    {s.label.split(" ")[0]}
                  </button>
                  {i < stages.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Notes</p>
            <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin mb-3">
              {lead.notes?.length === 0 && (
                <p className="text-xs text-slate-400 italic">No notes yet</p>
              )}
              {lead.notes?.map((n, i) => (
                <div key={i} className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-sm text-slate-700">{n.note}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">{new Date(n.at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="form-input flex-1 text-sm"
                placeholder="Add a note…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddNote(); } }}
              />
              <Button size="sm" onClick={onAddNote} disabled={!noteText.trim()}>Add</Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-5 py-4">
          <button
            onClick={() => onDelete(lead)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-red-200 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" /> Delete Lead
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
